import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { ProjectTypeField, mergeTypeLists, projectTypeLabel } from '../components/ProjectTypeField.jsx';
import { StageColorField } from '../components/StageColorField.jsx';
import { DEFAULT_TASK_COLUMNS, nextStageColor, stageColor, stageWash } from '../utils/taskProgress.js';
import { PROJECT_ROLES } from '../utils/taskQueue.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WEIGHTS = [
  [0, 'None'],
  [1, 'Standard'],
  [3, 'Large'],
  [8, 'Major'],
];

const EMPTY_TASK = { title: '', description: '', stage: 'open', progress_weight: 1, default_role: '', checklist: [] };

const CHECKLIST_LIMIT = 50;

let taskSeq = 0;

function taskRow(partial = {}) {
  taskSeq += 1;
  return { ...EMPTY_TASK, ...partial, _id: `task-${taskSeq}` };
}

function blankStages() {
  return DEFAULT_TASK_COLUMNS.map((stage) => ({ ...stage }));
}

function blankForm() {
  return {
    name: '',
    description: '',
    type: 'site',
    status: 'draft',
    notes: '',
    stages: blankStages(),
    tasks: [taskRow()],
  };
}

function stageKey(name, used) {
  const base = String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'stage';
  let key = base;
  let suffix = 2;
  while (used.has(key)) {
    key = `${base}-${suffix}`;
    suffix += 1;
  }
  used.add(key);
  return key;
}

function formFromTemplate(template) {
  const stages = (template.stages || []).length
    ? template.stages.map((stage, index) => ({
        key: stage.key,
        name: stage.name || '',
        color: stageColor(stage, index),
        progress_percent: Number(stage.progress_percent ?? 0),
        counts_toward_progress: stage.counts_toward_progress !== false,
        marks_complete: Boolean(stage.marks_complete),
        visible_to_client: stage.visible_to_client !== false,
      }))
    : blankStages();
  const firstStage = stages[0]?.key || 'open';
  return {
    name: template.name || '',
    description: template.description || '',
    type: template.type || 'site',
    status: template.status || 'draft',
    notes: template.notes || '',
    stages,
    tasks: (template.tasks || []).length
      ? template.tasks.map((task) => taskRow({
          title: task.title || '',
          description: task.description || '',
          stage: stages.some((stage) => stage.key === task.stage) ? task.stage : firstStage,
          progress_weight: Number(task.progress_weight ?? 1),
          default_role: task.default_role || '',
          checklist: (Array.isArray(task.checklist) ? task.checklist : [])
            .map((item) => (typeof item === 'string' ? item : (item?.title || '')))
            .filter((title) => String(title).trim() !== ''),
        }))
      : [],
  };
}

function statusLabel(value) {
  return STATUSES.find((status) => status.value === value)?.label || value || 'Draft';
}

function stageCaption(stage) {
  if (stage.marks_complete) return 'Done · 100%';
  if (!stage.counts_toward_progress) return 'Not counted';
  return `${Number(stage.progress_percent) || 0}%`;
}

function GripIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
      <path fill="currentColor" d="M5 3.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm0 4.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm0 4.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM11.5 3.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm0 4.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm0 4.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Z" />
    </svg>
  );
}

export function AdminProjectTemplateEditorPage() {
  const { templateId } = useParams();
  const isNew = templateId === 'new';
  const navigate = useNavigate();
  const location = useLocation();
  const typeFieldRef = useRef(null);
  const savedArrival = useRef(false);
  savedArrival.current = Boolean(location.state?.saved);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(!isNew);
  const [ready, setReady] = useState(isNew);
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    if (!location.state?.saved) return;
    setNotice('Template saved.');
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    setFieldErrors(undefined);
    setMissing(false);
    if (!savedArrival.current) setNotice('');

    apiJson('/api/admin/project-types')
      .then((incoming) => {
        if (!cancelled) setTypes((current) => mergeTypeLists(current, incoming));
      })
      .catch(() => {
        if (!cancelled) setTypes((current) => current);
      });

    if (isNew) {
      setForm(blankForm());
      setReady(true);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (!savedArrival.current) {
      setReady(false);
      setLoading(true);
    }
    apiJson('/api/admin/project-templates')
      .then((list) => {
        if (cancelled) return;
        const template = (list || []).find((item) => String(item.id) === String(templateId));
        if (!template) {
          setMissing(true);
          setReady(false);
          return;
        }
        setForm(formFromTemplate(template));
        setReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          setReady(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isNew, templateId]);

  const updateStage = (index, patch) => {
    setForm((current) => ({
      ...current,
      stages: current.stages.map((stage, stageIndex) => (stageIndex === index ? { ...stage, ...patch } : stage)),
    }));
  };

  const moveStage = (from, to) => {
    setForm((current) => {
      if (to < 0 || to >= current.stages.length || from === to) return current;
      const stages = [...current.stages];
      const [item] = stages.splice(from, 1);
      stages.splice(to, 0, item);
      return { ...current, stages };
    });
  };

  const markDone = (index) => {
    setForm((current) => ({
      ...current,
      stages: current.stages.map((stage, stageIndex) => ({
        ...stage,
        marks_complete: stageIndex === index,
        counts_toward_progress: stageIndex === index ? true : stage.counts_toward_progress,
        progress_percent: stageIndex === index ? 100 : stage.progress_percent,
      })),
    }));
  };

  const removeStage = (index) => {
    setForm((current) => {
      if (current.stages.length <= 1) return current;
      const removed = current.stages[index];
      let stages = current.stages.filter((_, stageIndex) => stageIndex !== index);
      if (!stages.some((stage) => stage.marks_complete)) {
        const last = stages.length - 1;
        stages = stages.map((stage, stageIndex) =>
          stageIndex === last
            ? { ...stage, marks_complete: true, counts_toward_progress: true, progress_percent: 100 }
            : stage,
        );
      }
      const fallback = stages[0]?.key;
      return {
        ...current,
        stages,
        tasks: current.tasks.map((task) => (task.stage === removed.key ? { ...task, stage: fallback } : task)),
      };
    });
  };

  const pipelineRef = useRef(null);

  const addStage = () => {
    setForm((current) => {
      if (current.stages.length >= 12) return current;
      const used = new Set(current.stages.map((stage) => stage.key));
      return {
        ...current,
        stages: [
          ...current.stages,
          {
            key: stageKey('New stage', used),
            name: 'New stage',
            color: nextStageColor(current.stages.map((stage) => stage.color)),
            progress_percent: 0,
            counts_toward_progress: true,
            marks_complete: false,
            visible_to_client: true,
          },
        ],
      };
    });
    requestAnimationFrame(() => {
      const pipeline = pipelineRef.current;
      if (pipeline) pipeline.scrollTo({ left: pipeline.scrollWidth, behavior: 'smooth' });
    });
  };

  const updateTask = (index, key, value) => {
    setForm((current) => ({
      ...current,
      tasks: current.tasks.map((task, taskIndex) => (taskIndex === index ? { ...task, [key]: value } : task)),
    }));
  };

  const moveTask = (from, to) => {
    setForm((current) => {
      if (to < 0 || to >= current.tasks.length || from === to) return current;
      const tasks = [...current.tasks];
      const [item] = tasks.splice(from, 1);
      tasks.splice(to, 0, item);
      return { ...current, tasks };
    });
  };

  const updateChecklist = (taskIndex, checklist) => {
    setForm((current) => ({
      ...current,
      tasks: current.tasks.map((task, index) => (index === taskIndex ? { ...task, checklist } : task)),
    }));
  };

  const addTask = () => {
    setForm((current) => {
      if (current.tasks.length >= 50) return current;
      return {
        ...current,
        tasks: [...current.tasks, taskRow({ stage: current.stages[0]?.key || 'open' })],
      };
    });
  };

  const onSave = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setFieldErrors(undefined);
    setSaving(true);
    let resolvedType = form.type;
    try {
      resolvedType = await typeFieldRef.current.resolve();
    } catch (err) {
      setError(err.message);
      setSaving(false);
      return;
    }
    const body = {
      name: form.name,
      description: form.description || null,
      type: resolvedType,
      status: form.status,
      notes: form.notes || null,
      stages: form.stages.map((stage) => ({
        key: stage.key,
        name: stage.name.trim(),
        color: stageColor(stage),
        progress_percent: stage.marks_complete ? 100 : Number(stage.progress_percent) || 0,
        counts_toward_progress: stage.marks_complete ? true : Boolean(stage.counts_toward_progress),
        marks_complete: Boolean(stage.marks_complete),
        visible_to_client: Boolean(stage.visible_to_client),
      })),
      tasks: form.tasks
        .map((task) => ({
          title: task.title.trim(),
          description: task.description.trim() || null,
          stage: task.stage,
          progress_weight: Math.max(0, Math.min(100, Math.round(Number(task.progress_weight) || 0))),
          default_role: task.default_role || null,
          checklist: (task.checklist || [])
            .map((title) => String(title).trim())
            .filter((title) => title !== '')
            .slice(0, CHECKLIST_LIMIT)
            .map((title) => ({ title })),
        }))
        .filter((task) => task.title !== ''),
    };
    try {
      if (isNew) {
        const created = await apiJson('/api/admin/project-templates', { method: 'POST', body });
        navigate(`/admin/project-templates/${created.id}`, { replace: true, state: { saved: true } });
      } else {
        await apiJson(`/api/admin/project-templates/${templateId}`, { method: 'PUT', body });
        setNotice('Template saved.');
      }
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSaving(false);
    }
  };

  const titledTasks = form.tasks.filter((task) => task.title.trim()).length;
  const pageTitle = isNew ? (form.name.trim() || 'New template') : (form.name.trim() || 'Edit template');

  return (
    <div className="tpl-page">
      <header className="tpl-head">
        <div className="tpl-head-copy">
          <Link to="/admin/project-templates" className="tpl-back">
            Project templates
          </Link>
          <h2 className="page-welcome-title" data-tour="page-title">{loading ? 'Template' : pageTitle}</h2>
        </div>
        <div className="row-actions">
          <Link
            to="/admin/project-templates"
            className="btn-secondary"
            onClick={(event) => {
              if (saving) event.preventDefault();
            }}
          >
            Cancel
          </Link>
          <button type="submit" form="template-editor" className="btn-primary" disabled={saving || loading || missing || !ready}>
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </div>
        {error || notice || fieldErrors ? (
          <div className="tpl-head-status">
            {error ? <p className="err">{error}</p> : null}
            {notice && !error ? <p className="success">{notice}</p> : null}
            <FieldErrors errors={fieldErrors} />
          </div>
        ) : null}
      </header>
      <p className="tpl-lead">A project started from this template copies these stages, weights, tasks, and their checklists.</p>

      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && missing ? (
        <div className="tpl-panel">
          <p style={{ marginTop: 0 }}>This template is no longer available.</p>
          <Link to="/admin/project-templates" className="btn-secondary">Back to templates</Link>
        </div>
      ) : null}
      {!loading && (missing || !ready) && error ? <p className="err">{error}</p> : null}

      {!loading && ready && !missing ? (
        <form id="template-editor" onSubmit={onSave}>
          <div className="tpl-layout">
            <section className="tpl-panel">
              <div className="tpl-section-head">
                <div>
                  <h3>Details</h3>
                  <p className="muted small">Name, type, and the status a new project starts in.</p>
                </div>
              </div>
              <div className="tpl-form-grid">
                <label className="field tpl-span-2">
                  <span>Name</span>
                  <input
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    required
                    autoComplete="off"
                    placeholder="Website launch"
                  />
                </label>
                <label className="field tpl-span-2">
                  <span>Description</span>
                  <input
                    value={form.description}
                    onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    placeholder="What this template is for"
                    autoComplete="off"
                  />
                </label>
                <div className="tpl-pair tpl-span-2">
                  <ProjectTypeField
                    ref={typeFieldRef}
                    value={form.type}
                    onChange={(type) => setForm((current) => ({ ...current, type }))}
                    types={types}
                    onTypesChange={setTypes}
                  />
                  <label className="field">
                    <span>Starting status</span>
                  <SearchableSelect
                    value={form.status}
                    onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                  >
                    {STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </SearchableSelect>
                  </label>
                </div>
                <label className="field tpl-span-2">
                  <span>Default notes</span>
                  <textarea
                    rows={4}
                    value={form.notes}
                    onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                    placeholder="Notes copied onto each new project"
                  />
                </label>
              </div>
            </section>

            <aside className="tpl-panel tpl-summary">
              <span className="eyebrow">At a glance</span>
              <dl className="tpl-stats">
                <div>
                  <dt>Type</dt>
                  <dd>{projectTypeLabel(types, form.type) || '—'}</dd>
                </div>
                <div>
                  <dt>Starts as</dt>
                  <dd>{statusLabel(form.status)}</dd>
                </div>
                <div>
                  <dt>Stages</dt>
                  <dd>{form.stages.length}</dd>
                </div>
                <div>
                  <dt>Starter tasks</dt>
                  <dd>{titledTasks}</dd>
                </div>
              </dl>
              <ol className="tpl-outline">
                {form.stages.map((stage, index) => {
                  const count = form.tasks.filter((task) => task.stage === stage.key && task.title.trim()).length;
                  return (
                    <li key={stage.key}>
                      <span>
                        <i className="tpl-outline-swatch" style={{ background: stageColor(stage, index) }} />
                        {stage.name.trim() || 'Untitled stage'}
                      </span>
                      <span className="muted small">
                        {stageCaption(stage)}
                        {count ? ` · ${count} task${count === 1 ? '' : 's'}` : ''}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </aside>
          </div>

          <section className="tpl-panel tpl-section">
            <div className="tpl-section-head">
              <div>
                <h3>Stages</h3>
                <p className="muted small">
                  The board columns for this project type. Pick a column color, then drag a stage or use the arrows to change the order. One stage is the done column and is always 100%.
                </p>
              </div>
              <button type="button" className="btn-secondary" onClick={addStage} disabled={form.stages.length >= 12}>
                Add stage
              </button>
            </div>
            <div className="tpl-pipeline" ref={pipelineRef}>
              {form.stages.map((stage, index) => {
                const percent = stage.marks_complete ? 100 : stage.counts_toward_progress ? Number(stage.progress_percent) || 0 : 0;
                const count = form.tasks.filter((task) => task.stage === stage.key && task.title.trim()).length;
                const color = stageColor(stage, index);
                return (
                  <article
                    key={stage.key}
                    className={`tpl-stage${stage.marks_complete ? ' is-done' : ''}${!stage.counts_toward_progress && !stage.marks_complete ? ' is-muted' : ''}${drag?.kind === 'stage' && drag.over === index ? ' is-drop' : ''}`}
                    style={{ background: stageWash(color, 0.9), borderColor: stageWash(color, 0.55) }}
                        onDragOver={(event) => {
                      if (drag?.kind !== 'stage') return;
                      event.preventDefault();
                      setDrag((current) => {
                        if (!current || current.kind !== 'stage' || current.over === index) return current;
                        return { ...current, over: index };
                      });
                    }}
                    onDrop={(event) => {
                      if (drag?.kind !== 'stage') return;
                      event.preventDefault();
                      moveStage(drag.from, index);
                      setDrag(null);
                    }}
                  >
                    <div className="tpl-stage-top">
                      <span
                        className="tpl-drag"
                        draggable
                        role="button"
                        tabIndex={0}
                        aria-label={`Reorder ${stage.name || 'stage'}`}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', String(index));
                          setDrag({ kind: 'stage', from: index, over: index });
                        }}
                        onDragEnd={() => setDrag(null)}
                      >
                        <GripIcon />
                      </span>
                      <span className="tpl-stage-count">Stage {index + 1} · {count} task{count === 1 ? '' : 's'}</span>
                      <div className="tpl-stage-moves">
                        <button type="button" className="tpl-icon-btn" aria-label="Move stage left" disabled={index === 0} onClick={() => moveStage(index, index - 1)}>
                          ←
                        </button>
                        <button type="button" className="tpl-icon-btn" aria-label="Move stage right" disabled={index === form.stages.length - 1} onClick={() => moveStage(index, index + 1)}>
                          →
                        </button>
                      </div>
                    </div>
                    <div className="tpl-stage-bar" aria-hidden>
                      <span style={{ width: `${Math.max(0, Math.min(100, percent))}%`, background: color }} />
                    </div>
                    <label className="field">
                      <span>Name</span>
                      <input
                        value={stage.name}
                        maxLength={80}
                        required
                        onChange={(event) => updateStage(index, { name: event.target.value })}
                      />
                    </label>
                    <StageColorField value={color} onChange={(next) => updateStage(index, { color: next })} label="Column color" />
                    <label className="field">
                      <span>Progress %</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        disabled={stage.marks_complete || !stage.counts_toward_progress}
                        value={percent}
                        onChange={(event) => updateStage(index, { progress_percent: Number(event.target.value) })}
                      />
                    </label>
                    <div className="tpl-toggles">
                      <label className="tpl-toggle">
                        <input
                          type="checkbox"
                          checked={stage.counts_toward_progress || stage.marks_complete}
                          disabled={stage.marks_complete}
                          onChange={(event) => updateStage(index, {
                            counts_toward_progress: event.target.checked,
                            progress_percent: event.target.checked ? stage.progress_percent : 0,
                          })}
                        />
                        Counts toward progress
                      </label>
                      <label className="tpl-toggle">
                        <input
                          type="radio"
                          name="template-done-stage"
                          checked={stage.marks_complete}
                          onChange={() => markDone(index)}
                        />
                        Done stage
                      </label>
                      <label className="tpl-toggle">
                        <input
                          type="checkbox"
                          checked={stage.visible_to_client}
                          onChange={(event) => updateStage(index, { visible_to_client: event.target.checked })}
                        />
                        Visible to client
                      </label>
                    </div>
                    <button type="button" className="btn-link tpl-remove" disabled={form.stages.length <= 1} onClick={() => removeStage(index)}>
                      Remove
                    </button>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="tpl-panel tpl-section">
            <div className="tpl-section-head">
              <div>
                <h3>Starter tasks</h3>
                <p className="muted small">
                  Created in this order on every new project. Empty rows are skipped. Checklist steps start unchecked.
                </p>
              </div>
              <button type="button" className="btn-secondary" onClick={addTask} disabled={form.tasks.length >= 50}>
                Add task
              </button>
            </div>
            {form.tasks.length === 0 ? (
              <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                No starter tasks yet. The project will still get these stages.
              </p>
            ) : (
              <div className="tpl-tasks">
                {form.tasks.map((task, index) => (
                  <article
                    key={task._id}
                    className={`tpl-task${drag?.kind === 'task' && drag.over === index ? ' is-drop' : ''}`}
                    onDragOver={(event) => {
                      if (drag?.kind !== 'task') return;
                      event.preventDefault();
                      setDrag((current) => {
                        if (!current || current.kind !== 'task' || current.over === index) return current;
                        return { ...current, over: index };
                      });
                    }}
                    onDrop={(event) => {
                      if (drag?.kind !== 'task') return;
                      event.preventDefault();
                      moveTask(drag.from, index);
                      setDrag(null);
                    }}
                  >
                    <div className="tpl-task-top">
                      <span
                        className="tpl-drag"
                        draggable
                        role="button"
                        tabIndex={0}
                        aria-label={`Reorder task ${index + 1}`}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', String(index));
                          setDrag({ kind: 'task', from: index, over: index });
                        }}
                        onDragEnd={() => setDrag(null)}
                      >
                        <GripIcon />
                      </span>
                      <span className="tpl-stage-num">Task {index + 1}</span>
                      <div className="tpl-stage-moves">
                        <button type="button" className="tpl-icon-btn" aria-label="Move task up" disabled={index === 0} onClick={() => moveTask(index, index - 1)}>
                          ↑
                        </button>
                        <button type="button" className="tpl-icon-btn" aria-label="Move task down" disabled={index === form.tasks.length - 1} onClick={() => moveTask(index, index + 1)}>
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => setForm((current) => ({
                            ...current,
                            tasks: current.tasks.filter((_, taskIndex) => taskIndex !== index),
                          }))}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="tpl-task-grid">
                      <label className="field">
                        <span>Title</span>
                        <input
                          value={task.title}
                          onChange={(event) => updateTask(index, 'title', event.target.value)}
                          placeholder="Kickoff call"
                          autoComplete="off"
                        />
                      </label>
                      <label className="field">
                        <span>Stage</span>
                        <SearchableSelect value={task.stage} onChange={(event) => updateTask(index, 'stage', event.target.value)}>
                          {form.stages.map((stage) => (
                            <option key={stage.key} value={stage.key}>
                              {stage.name || stage.key}
                            </option>
                          ))}
                        </SearchableSelect>
                      </label>
                      <label className="field">
                        <span>Default role</span>
                        <SearchableSelect value={task.default_role || ''} onChange={(event) => updateTask(index, 'default_role', event.target.value)}>
                          <option value="">Unassigned</option>
                          {PROJECT_ROLES.map((role) => (
                            <option key={role.id} value={role.id}>{role.label}</option>
                          ))}
                        </SearchableSelect>
                      </label>
                    </div>
                    <label className="field">
                      <span>Details</span>
                      <input
                        value={task.description}
                        onChange={(event) => updateTask(index, 'description', event.target.value)}
                        placeholder="Optional"
                        autoComplete="off"
                      />
                    </label>
                    <div className="tpl-weight">
                      <span>Progress impact</span>
                      <div className="weight-presets">
                        {WEIGHTS.map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            className={Number(task.progress_weight) === value ? 'is-selected' : ''}
                            onClick={() => updateTask(index, 'progress_weight', value)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <input
                        className="tpl-weight-input"
                        type="number"
                        min="0"
                        max="100"
                        aria-label="Progress impact"
                        value={task.progress_weight}
                        onChange={(event) => updateTask(index, 'progress_weight', event.target.value)}
                      />
                    </div>
                    <div className="tpl-checklist">
                      <span>Checklist</span>
                      {(task.checklist || []).length ? (
                        <ul>
                          {task.checklist.map((step, stepIndex) => (
                            <li key={`${task._id}-step-${stepIndex}`}>
                              <input
                                value={step}
                                placeholder="Step"
                                aria-label={`Checklist step ${stepIndex + 1} for task ${index + 1}`}
                                autoComplete="off"
                                maxLength={255}
                                onChange={(event) => updateChecklist(index, task.checklist.map((row, rowIndex) => (
                                  rowIndex === stepIndex ? event.target.value : row
                                )))}
                              />
                              <button
                                type="button"
                                className="btn-link"
                                onClick={() => updateChecklist(index, task.checklist.filter((_, rowIndex) => rowIndex !== stepIndex))}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <button
                        type="button"
                        className="btn-link"
                        disabled={(task.checklist || []).length >= CHECKLIST_LIMIT}
                        onClick={() => updateChecklist(index, [...(task.checklist || []), ''])}
                      >
                        Add step
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </form>
      ) : null}
    </div>
  );
}
